import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_URL = '/api';

function Status() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/status`);
        const data = await res.json();
        if (data.success) {
          setStatus(data);
        }
      } catch (err) {
        console.error('Status fetch error:', err);
      }
      setLoading(false);
    };

    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading status...</p>
      </div>
    );
  }

  return (
    <div className="status-page">
      {/* Header */}
      <header className="status-header">
        <div className="container">
          <div className="status-title">
            <Link to="/" className="nav-logo">
              <span className="logo-text">AVALW</span>
              <span className="logo-badge">Status</span>
            </Link>
          </div>
          <div className={`status-overall ${status?.operational ? 'operational' : 'degraded'}`}>
            <span className={`service-status-dot ${status?.operational ? 'operational' : 'degraded'}`}></span>
            {status?.operational ? 'All Systems Operational' : 'Partial Outage'}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="status-content">
        <div className="container">
          {/* Metrics */}
          <div className="status-metrics">
            <div className="status-metric">
              <div className="status-metric-value">{status?.uptime?.percentage || 99.9}%</div>
              <div className="status-metric-label">Uptime (30 days)</div>
            </div>
            <div className="status-metric">
              <div className="status-metric-value">{status?.latency?.average_ms || 0}ms</div>
              <div className="status-metric-label">Avg Response Time</div>
            </div>
            <div className="status-metric">
              <div className="status-metric-value">{status?.workers?.active || 0}</div>
              <div className="status-metric-label">Active Workers</div>
            </div>
            <div className="status-metric">
              <div className="status-metric-value">{(status?.requests?.today || 0).toLocaleString()}</div>
              <div className="status-metric-label">Requests Today</div>
            </div>
          </div>

          {/* Services */}
          <div className="services-list">
            <div className="services-header">Services Status</div>
            {(status?.services || []).map((service, i) => (
              <div key={i} className="service-item">
                <span className="service-name">{service.name}</span>
                <div className="service-status">
                  <span className={`service-status-dot ${service.status}`}></span>
                  <span style={{ textTransform: 'capitalize' }}>{service.status}</span>
                  <span className="service-latency">{service.latency}ms</span>
                </div>
              </div>
            ))}
          </div>

          {/* Uptime Details */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <div className="card-header">
              <h2>Uptime Details</h2>
            </div>
            <div style={{ padding: '1rem' }}>
              <table className="doc-table">
                <tbody>
                  <tr>
                    <td>Current Session Started</td>
                    <td>{formatDate(status?.uptime?.start)}</td>
                  </tr>
                  <tr>
                    <td>Uptime Duration</td>
                    <td>{status?.uptime?.days || 0} days, {status?.uptime?.hours || 0} hours</td>
                  </tr>
                  <tr>
                    <td>Total Accounts</td>
                    <td>{status?.workers?.total_accounts || 0}</td>
                  </tr>
                  <tr>
                    <td>Core-Hours Today</td>
                    <td>{status?.requests?.core_hours_today?.toFixed(1) || 0}</td>
                  </tr>
                  <tr>
                    <td>Last Updated</td>
                    <td>{timeAgo(status?.last_updated)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Incidents */}
          <div className="incidents-section">
            <h3 className="incidents-title">Recent Incidents</h3>
            {(status?.incidents || []).length === 0 ? (
              <div className="no-incidents">
                <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48" style={{ marginBottom: '1rem', color: '#10b981' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <p>No incidents reported in the last 90 days.</p>
              </div>
            ) : (
              status.incidents.map((incident, i) => (
                <div key={i} className="incident-item">
                  <div className="incident-header">
                    <span className={`incident-severity ${incident.severity}`}>{incident.severity}</span>
                    <span className="incident-title">{incident.title}</span>
                    <span className="incident-time">{timeAgo(incident.created_at)}</span>
                  </div>
                  {incident.description && (
                    <p className="incident-description">{incident.description}</p>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Status page auto-refreshes every 30 seconds.
              <br />
              <Link to="/" style={{ color: '#2563eb' }}>Back to AVALW Worker</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Status;
